/**
 * Provides all state for a subscription.
 *
 * Don't use the underlying data model directly as it is messy. Some fields
 * are interpreted differently depending on the subscription type, and some
 * fields are deprecated. It's simpler to always interpret the state using this.
 */
export type SubscriptionState = {
  type: SubscriptionType;

  /**
   * The end of the current billing period.
   *
   * For a stripe-managed plan:
   * - this is the next renewal date.
   * - the period is automatically incremented even if the invoice for the current
   * billing period hasn't been paid yet.
   * - it's not enough to only check the periodEnd to determine if the
   * subscription is active or not.
   * - if a subscription was cancelled through the stripe dashboard (e.g. to
   * process a refund request), then the periodEnd will be the cancellation /
   * refund date.
   * - if the subscription is scheduled to cancel, then it will be the scheduled
   * cancellation date.
   *
   * For a manual plan:
   * - it's the end date of the subscription, which is manually set.
   */
  periodEnd: Date;

  lifecycle: Lifecycle;

  entitlement: Entitlement;

  billing: BillingStatus;

  renewal: RenewalAction;

  status: Status;
};

export enum Lifecycle {
  /**
   * The subscription is still active and can be managed.
   * e.g. the subscription can be upgraded, downgraded, cancelled, renewed,
   * resolve billing issues, etc.
   */
  Active = "Active",

  /**
   * The subscription is in its final terminated state. It can no longer
   * be resumed. A new subscription must be created to replace it.
   *
   * The user is never entitled to use the subscription when in the Expired state.
   */
  Terminated = "Terminated",
}

/**
 * Has the current billing period been paid for yet, or not?
 *
 * If not, it indicates the user has a billing issue to resolve.
 */
export enum BillingStatus {
  /**
   * There are no billing issues.
   * - the current billing period has been paid, or
   * - the subscription has terminated.
   */
  Ok = "Ok",

  /**
   * The current billing period has not been paid.
   * There is a billing issue which requires action from the user to resolve.
   */
  Unpaid = "Unpaid",
}

/**
 * The simplified status of a subscription we show to users, derived entirely
 * from the other subscription state fields.
 *
 * Important: The status isn't enough to represent every possible state of a
 * subscription. For example, a subscription can have a downgrade scheduled and
 * also require action as the user hasn't paid for the current billing period.
 */
export enum Status {
  /**
   * User has a valid/ active plan which has been paid for the current billing
   * period. If it is a stripe-managed plan, then it will renew at the end
   * of the current billing period.
   */
  Active = "Active",

  /**
   * The plan is valid/ active but has been scheduled to downgrade to a lower
   * plan on renewal.
   */
  DowngradeScheduled = "Downgrade Scheduled",

  /**
   * Plan is valid/ active but has been scheduled for cancellation at the
   * end of the billing period instead of being renewed.
   */
  Cancelled = "Cancelled",

  /**
   * The user has an unpaid invoice for the current billing period.
   *
   * Important: A plan can also have a 'downgrade scheduled' or be 'cancelled'
   * while in this state, but the plan status will still be Requires Action as
   * it's the highest priority status.
   */
  RequiresAction = "Requires Action",

  /**
   * The plan has now expired / terminated. The subscription is in a finalised
   * state which cannot be renewed.
   *
   * A plan can't be renewed, resumed, cancelled, or downgraded while in this
   * state.
   *
   * The user must purchase a new subscription to continue using the software.
   */
  Expired = "Expired",
}

/**
 * Is the user entitled to use the subscription?
 */
export enum Entitlement {
  /**
   * The plan is considered to be in good standing.
   * - the invoice has been paid for the current billing period.
   */
  Entitled = "Entitled",

  /**
   * The plan is not considered to be in good standing.
   * - the initial subscription payment has not been paid, or,
   * - the renewal invoice has not been paid, or,
   * - the subscription has been terminated.
   */
  Suspended = "Suspended",
}

/**
 * What will happen to a subscription at the end of the current billing period.
 *
 * Manual subscriptions can only ever be in the Cancel or None state.
 */
export enum RenewalAction {
  /**
   * The subscription is scheduled to renew.
   */
  Renew = "Renew",

  /**
   * The subscription is scheduled to downgrade the plan and renew on the new
   * downgraded plan.
   */
  Downgrade = "Downgrade",

  /**
   * The subscription is scheduled to terminate (no renewal).
   *
   * A Manual subscription starts in this state.
   */
  Cancel = "Cancel",

  /**
   * The subscription will never reach another renewal date.
   * e.g. the plan has already terminated.
   */
  None = "None",
}

export enum SubscriptionType {
  /**
   * Manually created subscription which will always terminate on the end date.
   */
  Manual = "Manual",

  /**
   * Stripe-managed subscription
   */
  Stripe = "Stripe",
}
